<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Collections List</h1>
        <span class="page-subtitle">Manage experience collections</span>
      </div>
      <div class="page-header__right">
        <button class="btn-primary-action" @click="$router.push('/collections/create')">
          <i class="fa fa-plus"></i> Create Collection
        </button>
      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Slug</th>
            <th>Name</th>
            <th>Description</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in items" :key="item.id" class="dd-table__row">
            <td><span class="order-id">#{{ item.id }}</span></td>
            <td><span class="slug-cell">{{ item.slug }}</span></td>
            <td><span class="item-title">{{ item.title }}</span></td>
            <td>
                <span class="desc-cell" :title="item.short_description">
                  {{ item.short_description ? item.short_description.substr(0, 80) + '...' : '—' }}
                </span>
            </td>
            <td>
                <span :class="['status-badge', item.is_published ? 'status-badge--green' : 'status-badge--amber']">
                  <i :class="item.is_published ? 'fa fa-check-circle' : 'fa fa-clock-o'"></i>
                  {{ item.is_published ? 'Published' : 'Draft' }}
                </span>
            </td>
            <td>
              <div class="td-actions">
                <button class="btn-icon btn-icon--blue" title="Translate" @click="showTranslator(item.id)">
                  <i class="fa fa-language"></i>
                </button>
                <button class="btn-icon btn-icon--amber" title="Edit" @click="$router.push(`/collections/${item.id}`)">
                  <i class="fa fa-pencil"></i>
                </button>
                <button class="btn-icon btn-icon--red" title="Delete" @click="deleteCollection(item)">
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="items && items.length === 0">
            <td colspan="6" class="empty-state">
              <i class="fa fa-stack-overflow"></i>
              <div>No collections found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <translator
        ref="translator"
        :keys="translateKeys"
        model="Collection"
        :model_id="modelID"
    ></translator>

  </div>
</template>

<script>
import { config } from "../helpers";
import Translator from "@/components/Translator.vue";

export default {
  name: "Collections",
  components: { Translator },
  data() {
    return {
      url: "",
      items: [],
      translateKeys: [
        { key: "title",             type: "text"  },
        { key: "short_description", type: "text"  },
        { key: "description",       type: "text"  },
        { key: "need_to_know",      type: "text"  },
        { key: "highlights",        type: "array" },
      ],
      modelID: 0,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/collections";
    this.loadData();
  },

  methods: {
    loadData() {
      axios.get(this.url).then((r) => { this.items = r.data.data; });
    },

    deleteCollection(collection) {
      if (confirm(`Do you want to delete "${collection.title}"?`)) {
        axios.delete(`${this.url}/${collection.id}`).then(() => this.loadData());
      }
    },

    showTranslator(modelId) {
      this.modelID = parseInt(modelId);
      this.$refs.translator.showTranslateModal();
    },
  },
};
</script>

<style scoped>
.page-wrap    { display: flex; flex-direction: column; gap: 16px; }
.page-header  { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }
.page-header__right { display: flex; align-items: center; gap: 8px; }

.btn-primary-action { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-primary-action:hover { opacity: .88; }

/* Table */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; white-space: nowrap; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table tr:last-child td { border-bottom: none; }

.order-id  { font-weight: 500; color: var(--text-1); }
.item-title { font-weight: 500; color: var(--text-1); }
.slug-cell  { font-family: monospace; font-size: 12px; color: var(--text-3); background: var(--bg-hover); padding: 2px 7px; border-radius: 5px; border: 1px solid var(--border); }
.desc-cell  { font-size: 12px; color: var(--text-3); cursor: help; max-width: 260px; display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Action buttons */
.td-actions { display: flex; gap: 6px; }
.btn-icon { width: 30px; height: 30px; border-radius: 7px; border: 1px solid var(--border); background: var(--bg-hover); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 13px; color: var(--text-2); transition: border-color .15s, color .15s, background .15s; }
.btn-icon--blue:hover  { border-color: #818cf8; color: #818cf8; background: rgba(129,140,248,.08); }
.btn-icon--amber:hover { border-color: #f59e0b; color: #f59e0b; background: rgba(245,158,11,.08); }
.btn-icon--red:hover   { border-color: var(--accent); color: var(--accent); background: rgba(244,47,75,.08); }

/* Empty */
.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }
</style>